import { z } from 'zod';
import { ancestorsSchema, treeChildrenNodesSchema } from '@/types/tree';
import { paginationSchema } from '@/types/pagination';
import { keywordSchema } from '@/types/keyword';
import { assetNoteSchema } from '@/types/notes';

export enum Variants {
  ThumbnailInternal = 'thumbnail-internal',
  ThumbnailInternalMedium = 'thumbnail-internal-medium',
  PreviewInternal = 'preview-internal',
  VideoPreviewInternal = 'video-preview-internal',
}

export enum ScanStatus {
  Unscanned = 'unscanned',
  Clean = 'clean',
  Quarantined = 'quarantined',
  Completed = 'completed',
}

export const ScanStatusSchema = z.nativeEnum(ScanStatus).optional();

// A tag as attached to an asset: the parent name is display-only context, the
// child (`name`) is the tag identity used everywhere tags are read or written.
//
// TEMPORARY BACKWARD-COMPAT BRIDGE: until every deploy sends the new `{ name, parent }`
// shape, the backend may still return the pre-migration flat tag name string. Normalize
// that into a parentless tag object here so nothing downstream has to know the old shape
// ever existed. Delete the `z.preprocess` wrapper (keep the `z.object` below) once the
// backend consistently returns tag objects.
export const assetTagSchema = z.preprocess(
  (tag) => (typeof tag === 'string' ? { name: tag, parent: null } : tag),
  z.object({
    /** Optional: the string-normalized branch above produces none. */
    id: z.string().optional(),
    name: z.string(),
    parent: z.string().nullable(),
  }),
);

export type AssetTag = z.infer<typeof assetTagSchema>;

const focalPointSchema = z
  .object({
    x: z.number(),
    y: z.number(),
  })
  .optional()
  .nullable();

export const baseAssetVersionSchema = z.object({
  id: z.string(),
  active: z.boolean(),
  originalFileName: z.string(),
  fileSize: z.number(),
  width: z.number(),
  height: z.number(),
  fileType: z.string(),
  thumbnail: z.string().url().optional(),
  // Signed URL for this version's own `preview-internal` variant. Omitted (not null) when the
  // version has no preview variant, and absent entirely until the backend ships it — so every
  // consumer must fall back to `thumbnail`.
  preview: z.string().url().optional(),
  mimeType: z.string().optional(),
  url: z.string().url().optional(),
  focalPoint: focalPointSchema,
  detectedFocalPoint: focalPointSchema,
});

export type BaseAssetVersion = z.infer<typeof baseAssetVersionSchema>;

/**
 * A single metadata _value_ can be:
 * - a string
 * - a number
 * - a boolean
 * - an array of strings
 * - a Date (or ISO string → Date)
 * - empty (null / undefined)
 */
const metadataValueSchema = z
  .union([z.string().nullish(), z.boolean(), z.number().optional(), z.array(z.string()), z.coerce.date()])
  .nullable();

export const assetWorkflowMetadataSchema = z
  .union([z.array(z.never()).transform(() => ({}) as Record<string, any>), z.record(z.string(), metadataValueSchema)])
  .optional();

export const fileMetadataEntrySchema = z.object({
  description: z.string().optional().nullable(),
  value: metadataValueSchema,
});

export type FileMetadataEntry = z.infer<typeof fileMetadataEntrySchema>;

export const fileMetadataSchema = z
  .union([z.record(z.string(), fileMetadataEntrySchema), z.array(z.any()), z.undefined()])
  .transform((val) => (Array.isArray(val) || val === undefined ? {} : val))
  .optional();

export type FileMetadata = z.infer<typeof fileMetadataSchema>;

export const assetVariantsSchema = z.union([
  z.tuple([]),
  z.record(
    z.string(),
    z
      .object({
        width: z.number(),
        height: z.number(),
        url: z.string().nullable(),
        type: z.string().optional().nullable(),
        name: z.string().optional().nullable(),
        watermark: z.string().optional().nullable(),
        automatic: z.boolean().optional().nullable(),
        public: z.boolean().optional().nullable(),
        internal: z.boolean().optional().nullable(),
        format: z.string().optional().nullable(),
      })
      .nullable(),
  ),
]);

export const assetSchema = z.object({
  createdAt: z.string().optional(),
  trashedAt: z.string().optional().nullable(),
  createdBy: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .optional(),
  description: z.string().optional().nullable(),
  destinations: z.string().array().optional().nullable(),
  fileMetadata: fileMetadataSchema.optional().nullable(),
  mimeType: z.string().optional().nullable(),
  size: z.number().optional().nullable(),
  focalPoint: focalPointSchema,
  detectedFocalPoint: focalPointSchema,
  fileName: z.string().optional().nullable(),
  parentFolderName: z.string().optional().nullable(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  id: z.string(),
  assetId: z.string().optional().nullable(),
  name: z.string(),
  type: z.string().nullable().optional(),
  slug: z.string(),
  tags: z.array(assetTagSchema).optional().nullable(),
  tenantId: z.string().optional().nullable(),
  nodeId: z.string(),
  parentFolderId: z.string().optional().nullable(),
  keywords: z.array(keywordSchema).optional().nullable(),
  updatedAt: z.string().optional().nullable(),
  metadata: assetWorkflowMetadataSchema,
  ancestors: ancestorsSchema.nullable().optional(),
  albumPaths: z.array(z.string()).optional().nullable(),
  rating: z.number().optional(),
  favorite: z.boolean().optional(),
  position: z.number().optional().nullable(),
  variants: assetVariantsSchema
    .optional()
    // api will return an empty array if there are no variants,
    // transform it into an empty object instead, to make it easier to work with
    .transform((arg) => (Array.isArray(arg) || arg === undefined ? {} : arg)),
  workflows: assetWorkflowMetadataSchema,
  versions: z.array(baseAssetVersionSchema).optional(),
  scanStatus: ScanStatusSchema,
  trashed: z.boolean().optional(),
  // Client-side only, never sent by the API. Set when a broadcast lands for a row still on
  // screen, and cleared by the refresh that removes the row. Optional because an asset only
  // carries one while it is in that short-lived state.
  //
  // `departed` says the row no longer belongs in the list being viewed, and why: `trashed`
  // in a folder, `restored` in the bin. Carrying the reason rather than a boolean means the
  // thumbnail does not have to ask the router which list it is in.
  //
  // `deleted` is permanent. It wins over `departed`, which is cleared when it is set.
  departed: z.enum(['trashed', 'restored']).optional(),
  deleted: z.boolean().optional(),
  notes: z.array(assetNoteSchema).optional(),
  commentCount: z.number().optional().nullable(),
  url: z.string().url().optional().nullable(),
  originalName: z.string().optional().nullable(),
});

export const assetIdNameSchema = assetSchema.pick({ id: true, name: true, type: true });
export type AssetIdNameTypes = z.infer<typeof assetIdNameSchema>;

export type Asset = z.infer<typeof assetSchema>;

/**
 * No version yet means loading, regardless of why. A version whose thumbnail variant is
 * present but null means still generating one; undefined means none was ever expected.
 */
export const isAssetLoading = (asset?: { type?: string | null; variants?: unknown }): boolean => {
  const variants = asset?.variants;
  const thumbnail =
    variants && !Array.isArray(variants) && typeof variants === 'object'
      ? (variants as Record<string, { url?: string | null } | null>)[Variants.ThumbnailInternal]
      : undefined;

  if (thumbnail?.url) {
    return false;
  }
  if (!asset?.type) {
    return true;
  }
  return thumbnail === null;
};

export const getAssetsSchema = z.object({
  assets: z.array(assetSchema),
  pagination: paginationSchema.optional(),
});

export type GetAssets = z.infer<typeof getAssetsSchema>;

const assetsRootAssetsSchema = z.preprocess((value) => value ?? { assets: [] }, getAssetsSchema);

// Created new schema because response of folders and albums api is different from assets api
export const getAssetsRootSchema = z.object({
  nodeId: z.string().optional(),
  name: z.string().optional(),
  slug: z.string(),
  assets: assetsRootAssetsSchema,
  ancestors: ancestorsSchema,
  favorite: z.boolean(),
  // Present only when the request asked for `includeChildren=1`.
  children: treeChildrenNodesSchema.optional(),
});

export type GetAssetsRoot = z.infer<typeof getAssetsRootSchema>;

const UpdateAssetParams = assetSchema
  .pick({ name: true, description: true, slug: true, rating: true })
  .extend({ tags: z.string().array(), tagIds: z.string().array() })
  .partial({ name: true, description: true, slug: true, tags: true, tagIds: true, rating: true });

export type UpdateAssetParams = z.infer<typeof UpdateAssetParams>;

export const updateAssetReturnSchema = assetSchema.omit({ variants: true });

export type UpdateAssetReturn = z.infer<typeof updateAssetReturnSchema>;

export type DuplicateAssetFn = (params: { id: string; destinationFolderId: string }) => Promise<UpdateAssetReturn>;

export const deleteAssetVersionReturnSchema = assetSchema.pick({ id: true });

export const activateAssetVersionReturnSchema = baseAssetVersionSchema.extend({
  width: z.number().optional(),
  height: z.number().optional(),
  createdAt: z.string().optional(),
  assetId: z.string().optional(),
});

export type AssetWorkflowMetadata = z.infer<typeof assetWorkflowMetadataSchema>;

export const getMultipleAssetsSchema = z.object({
  assetsSelected: z.number(),
  metadata: assetWorkflowMetadataSchema,
  workflows: assetWorkflowMetadataSchema,
  tags: z.object({
    isFullIntersection: z.boolean(),
    tags: z.array(assetTagSchema),
  }),
});

const UpdateAssetVersionParams = baseAssetVersionSchema.pick({ focalPoint: true }).partial({ focalPoint: true });

export type UpdateAssetVersionParams = z.infer<typeof UpdateAssetVersionParams>;

export const variantUrlsSchema = z.record(z.string(), z.string());
